import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { api } from '@shared/api/client';
import { key } from '@shared/api/query-keys';

import type { TagTerm } from '../_shared/types';

/**
 * Create, rename, and a delete confirmed by name — the three the catalogue
 * grants, and no fourth.
 *
 * **No parent and no ordering**, because those rows name categories only: a tag
 * has no rank, no parent, and no path, and the write bodies say so by carrying
 * one field.
 *
 * **No merge-tags and no rename-across-titles action.** Neither has a row. A
 * rename already reaches every title holding the tag — the dialog's hint states
 * that consequence rather than adding a control for it.
 *
 * **Mutations never auto-retry**, and `Idempotency-Key` is not sent: the header
 * is scoped platform-wide to finance-bound money-moving writes and this service
 * accepts it nowhere. Retry safety comes from the unique index on the
 * **normalized** name — «أفلام» and «افلام» are one tag, and letting both exist
 * would split every listing that uses it.
 */

function invalidateVocabularies(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: key('content', 'tags') });
  void queryClient.invalidateQueries({ queryKey: key('content', 'categories') });
}

export function useCreateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { name: string }) => api.post<TagTerm>('content', '/admin/tags', input),
    onSuccess: () => invalidateVocabularies(queryClient),
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tagId, name }: { tagId: string; name: string }) =>
      api.patch<TagTerm>('content', `/admin/tags/${tagId}`, { name }),
    onSuccess: () => invalidateVocabularies(queryClient),
  });
}

/**
 * `confirm=true` is the wire half of the dialog, and this hook is called from a
 * `<ConfirmDialog>` accept handler and from nowhere else.
 */
export function useDeleteTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tagId: string) => api.delete<null>('content', `/admin/tags/${tagId}?confirm=true`),
    onSuccess: () => invalidateVocabularies(queryClient),
  });
}
